import { createContext, useContext, useEffect, useState, type FormEvent } from 'react';
import { useTenant } from '../app';
import { Kicker, Link } from '../components';
import { navigate, usePath } from '../router';
import { adminApi, errorText, type Identity, type TenantSettings } from './api';
import { CoursesPage } from './courses';
import { CustomerPage, CustomersPage } from './customers';
import { LessonEditor } from './lesson';
import { MailsPage } from './mails';
import { ProductsPage } from './products';
import { DesignPage, LegalPage, SettingsPage } from './settings';
import { ConnectPage } from './connect';
import './admin.css';

/**
 * Die Verwaltung eines Anbieters unter /verwaltung (Konzept 5). Eigener Login (E-Mail + Passwort), Seitenleiste,
 * Seiten: Kurse, Produkte, Kunden, Erscheinungsbild, E-Mails, Rechtstexte, Einstellungen.
 */

interface AdminSession {
  identity: Identity;
  setTenant: (t: TenantSettings) => void;
  refresh: () => Promise<void>;
}

const AdminContext = createContext<AdminSession | null>(null);
export const useAdmin = (): AdminSession => useContext(AdminContext)!;

type Route =
  | { page: 'courses' }
  | { page: 'course'; id: string }
  | { page: 'lesson'; courseId: string; id: string }
  | { page: 'products' }
  | { page: 'customers' }
  | { page: 'customer'; id: string }
  | { page: 'design' }
  | { page: 'mails' }
  | { page: 'legal' }
  | { page: 'connect' }
  | { page: 'settings' };

function routeFor(pathname: string): Route {
  const p = pathname.replace(/^\/verwaltung/, '').replace(/\/+$/, '') || '/';
  let m: RegExpExecArray | null;
  if ((m = /^\/kurse\/([0-9a-f-]{36})\/lektion\/([0-9a-f-]{36})$/.exec(p))) return { page: 'lesson', courseId: m[1]!, id: m[2]! };
  if ((m = /^\/kurse\/([0-9a-f-]{36})$/.exec(p))) return { page: 'course', id: m[1]! };
  if (p === '/produkte') return { page: 'products' };
  if ((m = /^\/kunden\/([0-9a-f-]{36})$/.exec(p))) return { page: 'customer', id: m[1]! };
  if (p === '/kunden') return { page: 'customers' };
  if (p === '/design') return { page: 'design' };
  if (p === '/mails') return { page: 'mails' };
  if (p === '/rechtstexte') return { page: 'legal' };
  if (p === '/ki') return { page: 'connect' };
  if (p === '/einstellungen') return { page: 'settings' };
  return { page: 'courses' };
}

const NAV: { href: string; label: string; match: (r: Route) => boolean }[] = [
  { href: '/verwaltung', label: 'Kurse', match: (r) => r.page === 'courses' || r.page === 'course' || r.page === 'lesson' },
  { href: '/verwaltung/produkte', label: 'Produkte & Preise', match: (r) => r.page === 'products' },
  { href: '/verwaltung/kunden', label: 'Kunden', match: (r) => r.page === 'customers' || r.page === 'customer' },
  { href: '/verwaltung/design', label: 'Erscheinungsbild', match: (r) => r.page === 'design' },
  { href: '/verwaltung/mails', label: 'E-Mails', match: (r) => r.page === 'mails' },
  { href: '/verwaltung/rechtstexte', label: 'Rechtstexte', match: (r) => r.page === 'legal' },
  { href: '/verwaltung/ki', label: 'KI verbinden', match: (r) => r.page === 'connect' },
  { href: '/verwaltung/einstellungen', label: 'Einstellungen', match: (r) => r.page === 'settings' },
];

export function AdminApp() {
  const tenant = useTenant();
  const path = usePath();
  const route = routeFor(path);
  const [identity, setIdentity] = useState<Identity | null | undefined>(undefined);

  const refresh = async () => {
    try {
      setIdentity(await adminApi.me());
    } catch {
      setIdentity(null);
    }
  };
  useEffect(() => {
    void refresh();
    document.title = `Verwaltung – ${tenant.name}`;
  }, [tenant.name]);

  if (identity === undefined) return null;
  if (identity === null) return <AdminLogin onLoggedIn={setIdentity} />;

  const session: AdminSession = { identity, setTenant: (t) => setIdentity({ ...identity, tenant: t }), refresh };

  let content: React.ReactNode;
  switch (route.page) {
    case 'course':
      content = <CoursesPage courseId={route.id} />;
      break;
    case 'lesson':
      content = <LessonEditor courseId={route.courseId} lessonId={route.id} />;
      break;
    case 'products':
      content = <ProductsPage />;
      break;
    case 'customers':
      content = <CustomersPage />;
      break;
    case 'customer':
      content = <CustomerPage id={route.id} />;
      break;
    case 'design':
      content = <DesignPage />;
      break;
    case 'mails':
      content = <MailsPage />;
      break;
    case 'legal':
      content = <LegalPage />;
      break;
    case 'connect':
      content = <ConnectPage />;
      break;
    case 'settings':
      content = <SettingsPage />;
      break;
    default:
      content = <CoursesPage courseId={null} />;
  }

  async function signOut() {
    await adminApi.logout().catch(() => undefined);
    setIdentity(null);
    navigate('/verwaltung');
  }

  return (
    <AdminContext.Provider value={session}>
      <div className="adm">
        <aside className="adm__side">
          <div className="adm__brand">
            <Kicker>Verwaltung</Kicker>
            <strong>{identity.tenant.name}</strong>
          </div>
          <nav className="adm__nav">
            {NAV.map((n) => (
              <Link key={n.href} href={n.href} className={`adm__navlink${n.match(route) ? ' adm__navlink--active' : ''}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="adm__foot">
            <span className="muted small">{identity.admin.email}</span>
            <a href="/" className="small" target="_blank" rel="noopener">
              Akademie ansehen ↗
            </a>
            <button type="button" className="link-button small" onClick={() => void signOut()}>
              Abmelden
            </button>
          </div>
        </aside>
        <main className="adm__main">{content}</main>
      </div>
    </AdminContext.Provider>
  );
}

function AdminLogin({ onLoggedIn }: { onLoggedIn: (i: Identity) => void }) {
  const tenant = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLoggedIn(await adminApi.login(email, password));
    } catch (err) {
      setError(errorText(err, 'E-Mail oder Passwort stimmen nicht.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page page--narrow">
      <form className="card card--login form" onSubmit={submit}>
        <Kicker>{tenant.name}</Kicker>
        <h1 className="login__title">Verwaltung</h1>
        <div className="field">
          <label htmlFor="adm-email">E-Mail-Adresse</label>
          <input id="adm-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label htmlFor="adm-password">Passwort</label>
          <input id="adm-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button type="submit" className="button button--large" disabled={busy}>
          {busy ? 'Bitte warten …' : 'Anmelden'}
        </button>
        <p className="muted small">
          Du bist Kunde und suchst deine Kurse? <Link href="/anmelden">Zur Anmeldung für Lernende</Link>.
        </p>
      </form>
    </main>
  );
}
